import { styled } from "toucan";
import { PlayingBars } from "./PlayingBars.tsx";
import { albumForUri } from "../model.ts";
import { store } from "../state.ts";
import type { Playing } from "../playback.ts";

const Indicator = styled("div")`
  display: flex;
  align-items: center;
  min-width: 0;

  /* The sleeve and the album's name are one control: the header is the way
     back to the record playing, from wherever browsing has taken you. */
  .playing {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    box-shadow: none;
    color: inherit;
    font: inherit;
    text-align: left;
  }

  button.playing {
    cursor: pointer;
  }

  .cover {
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    object-fit: cover;
  }

  .cover.placeholder {
    display: grid;
    place-items: center;
    background: var(--toucan-surface-sunken);
    color: var(--toucan-muted);
  }

  /* Room for a long title in full on a desktop header. */
  .album-name {
    overflow: hidden;
    max-width: 22rem;
    color: var(--toucan-ink);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .starting {
    flex: none;
    color: var(--toucan-muted);
    white-space: nowrap;
  }

  /* A phone's header already has a title and the "Play on" pill, so the name
     goes: the sleeve says which record it is, and the bars say it is on. */
  @media (max-width: 480px) {
    .album-name {
      display: none;
    }

    /* "Starting…" would run under the pill too, so it is kept only for a
       screen reader and the sleeve is dimmed instead. */
    .starting {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
    }

    .cover:has(~ .starting) {
      opacity: 0.5;
    }
  }
`;

/**
 * The record just asked for, in the header, until Spotify reports it. A play
 * crosses the network and may first load the SDK, which is long enough for a
 * silent header to look like a missed press.
 */
export function StartingIndicator({
  album,
}: {
  album: { name: string; image: string | undefined };
}) {
  return (
    <Indicator>
      <span class="playing">
        {album.image === undefined ? (
          <span class="cover placeholder" aria-hidden="true">
            ♪
          </span>
        ) : (
          <img class="cover" src={album.image} alt="" />
        )}
        <span class="album-name">{album.name}</span>
        <span class="starting" role="status">
          Starting…
        </span>
      </span>
    </Indicator>
  );
}

/**
 * What is playing, in the header: the sleeve, the album's name and the bars.
 *
 * It names the album rather than the track because tapping it opens the
 * album's page. It has no play/pause, since two targets don't fit in this
 * room and the transport is on the album's page.
 */
export function PlayingIndicator({
  playing,
  onPage,
  onShowAlbum,
}: {
  playing: Playing;
  /** Whether the record playing is the page already on screen. */
  onPage: boolean;
  onShowAlbum: (albumId: string) => void;
}) {
  // On the album's own page there is nowhere to go, so only the bars stay.
  if (onPage) {
    return (
      <Indicator>
        <PlayingBars paused={playing.paused} />
      </Indicator>
    );
  }
  const inside = (
    <>
      {playing.cover === undefined ? (
        <span class="cover placeholder" aria-hidden="true">
          ♪
        </span>
      ) : (
        <img class="cover" src={playing.cover} alt="" />
      )}
      <span class="album-name">{playing.albumName}</span>
      <PlayingBars paused={playing.paused} />
    </>
  );
  // A button only when the library holds the record, since otherwise it has
  // no page to open.
  const album = albumForUri(store(), playing.uri);
  return (
    <Indicator>
      {album === undefined ? (
        <span class="playing">{inside}</span>
      ) : (
        <button
          type="button"
          class="playing"
          aria-label={`Show ${playing.albumName}`}
          onClick={() => {
            onShowAlbum(album.id);
          }}
        >
          {inside}
        </button>
      )}
    </Indicator>
  );
}
